import { CheckCircleOutline } from "@mui/icons-material";
import {
	Avatar,
	Box,
	Button,
	Dialog,
	DialogContent,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";

import {
	type ConsultantOption,
	ParticipantType,
	type ParticipantTypeValue,
} from "CommissionDistribution/Models/types";
import {
	CommissionDistributionResource,
	useCommissionDistributionResource,
} from "CommissionDistribution/Translations/CommissionDistributionResource";

export type SummaryParticipant = {
	consultant: ConsultantOption | null;
	participantType: ParticipantTypeValue;
	amount: number;
};

type Props = {
	open: boolean;
	isConfirmed: boolean;
	participants: SummaryParticipant[];
	onClose: () => void;
};

export const CommissionDistributionSuccessDialog = ({
	open,
	isConfirmed,
	participants,
	onClose,
}: Props) => {
	const { t } = useCommissionDistributionResource();
	const R = CommissionDistributionResource;
	const theme = useTheme();

	const title = isConfirmed
		? t(R.SuccessDialog.ConfirmedTitle)
		: t(R.SuccessDialog.SavedTitle);
	const subtitle = isConfirmed
		? t(R.SuccessDialog.ConfirmedSubtitle)
		: t(R.SuccessDialog.SavedSubtitle);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<Box
				sx={{
					background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
					color: theme.palette.primary.contrastText,
					p: 3,
					textAlign: "center",
				}}
			>
				<CheckCircleOutline sx={{ fontSize: 48, mb: 1 }} />
				<Typography variant="h6" fontWeight={800}>
					{title}
				</Typography>
				<Typography
					variant="body2"
					sx={{ opacity: 0.85, color: theme.palette.primary.contrastText }}
				>
					{subtitle}
				</Typography>
			</Box>
			<DialogContent>
				<Typography
					variant="caption"
					sx={{
						textTransform: "uppercase",
						fontWeight: 700,
						color: "text.secondary",
						display: "block",
						mb: 1,
					}}
				>
					{t(R.SuccessDialog.SummaryTitle)}
				</Typography>
				<Stack
					divider={
						<Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }} />
					}
				>
					{participants.map((p, idx) => (
						<Box
							key={`${p.consultant?.consultantId ?? idx}-${p.participantType}`}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								py: 1,
							}}
						>
							<Avatar sx={{ width: 28, height: 28, fontSize: 11 }}>
								{getInitials(p.consultant?.fullName)}
							</Avatar>
							<Box sx={{ flexGrow: 1, minWidth: 0 }}>
								<Typography variant="body2" fontWeight={700}>
									{p.consultant?.fullName ?? "—"}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{getRoleLabel(p.participantType, t)}
								</Typography>
							</Box>
							<Typography variant="h6" fontWeight={800} color="primary">
								{p.amount} %
							</Typography>
						</Box>
					))}
				</Stack>
				<Button
					onClick={onClose}
					variant="contained"
					color="primary"
					fullWidth
					sx={{ mt: 2 }}
				>
					{t(R.SuccessDialog.BackToContract)}
				</Button>
			</DialogContent>
		</Dialog>
	);
};

const getInitials = (fullName?: string): string => {
	if (!fullName) return "?";
	const parts = fullName.trim().split(/\s+/);
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
};

const getRoleLabel = (
	type: ParticipantTypeValue,
	t: ReturnType<typeof useCommissionDistributionResource>["t"],
): string => {
	const R = CommissionDistributionResource;
	switch (type) {
		case ParticipantType.Realizator:
			return t(R.Types.Realizator.Name);
		case ParticipantType.ContractTipster:
			return t(R.Types.ContractTipster.Name);
		case ParticipantType.ClientTipster:
			return t(R.Types.ClientTipster.Name);
		case ParticipantType.InvitedSpecialist:
			return t(R.Types.InvitedSpecialist.Name);
		default:
			return "";
	}
};
